import { RoundState, type RoundSummary } from "./rounds";

/**
 * What a room is doing right now, as the menu says it next to each room (owner request, October 2026): a round being
 * played ("In progress"), the next one counting down ("Starting 0:45"), full, open for players, paused (registration
 * stopped by an admin), or not open yet. Pure: from the public room status the menu already reads.
 */
export type RoomStateKind = "coming_soon" | "paused" | "in_progress" | "starting" | "full" | "open";

export interface RoomStateView {
	readonly kind: RoomStateKind;
	/** a few words, for the room list and picker: "In progress", "Starting 0:45", "Full", "Open · 4/10" (13 characters at most) */
	readonly label: string;
	/** a sentence for the room's line: who's in it and what happens next */
	readonly detail: string;
}

export interface RoomStateInput {
	readonly availability: "available" | "coming_soon";
	/** an admin stopped registration (no new players, rounds already full or playing go on) */
	readonly registrationPaused?: boolean;
	readonly activeRound?: Pick<RoundSummary, "state" | "playingCount" | "registeredCount">;
	readonly nextRound?: Pick<RoundSummary, "state" | "registeredCount" | "startsAt">;
	/** the player looking is in this room's round being played (or was, and is out of it): they register again once it ends */
	readonly inRound?: boolean;
}

/**
 * The words the menu says them in: a phrase in English with its `<name>`s, and the values to put in. Without the
 * menu's own (the phrasebook's `t`, client/src/scripts/utils/translations/phrases.ts, 1.04.4), English as written. Each
 * phrase is a whole label or sentence (client/vite/plugins/phraseExtractor.ts reads them from this file)
 */
export type RoomStateWords = (phrase: string, values?: Readonly<Record<string, string | number>>) => string;

const english: RoomStateWords = (phrase, values) =>
	phrase.replace(/<([A-Za-z_]\w*)>/gu, (whole, name: string) => (values && Object.hasOwn(values, name) ? String(values[name]) : whole));

/** "0:45" (a minute or more: "1:20") */
export const countdownText = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** the room's state now (`now`: the server's clock, ms), in the menu's words (`t`: English without them) */
export function roomStateOf(
	room: RoomStateInput,
	now: number,
	limits: { readonly minPlayers: number; readonly maxPlayers: number },
	t: RoomStateWords = english,
): RoomStateView {
	if (room.availability === "coming_soon") return { kind: "coming_soon", label: t("Coming soon"), detail: t("This room isn't open yet.") };
	const { minPlayers: min, maxPlayers: max } = limits;
	const next = room.nextRound;
	const registered = Math.max(0, next?.registeredCount ?? 0);
	const full = registered >= max;
	const counting = next?.state === RoundState.Countdown && next.startsAt !== undefined;
	const left = counting ? Math.max(0, Math.ceil(((next?.startsAt ?? now) - now) / 1000)) : 0;
	const playing = room.activeRound?.state === RoundState.InProgress;
	const paused = room.registrationPaused === true;
	const players = (count: number): string => (count === 1 ? t("1 player") : t("<n> players", { n: count }));
	if (playing) {
		const inIt = players(room.activeRound?.playingCount || room.activeRound?.registeredCount || 0);
		// (no seat left for a new player: said first, the round being played in the sentence)
		const detail = full
			? t("A round is being played (<players>). The next round is full too: it starts when this one ends.", { players: inIt })
			: paused
				? t("A round is being played (<players>). Registration for the next round is paused.", { players: inIt })
				: counting
					? t("A round is being played (<players>). The next round has <registered>/<max> players and starts when this one ends.", { players: inIt, registered, max })
					: room.inRound
						? t("A round is being played (<players>). You can register for the next round once this one ends.", { players: inIt })
						: registered
							? t("A round is being played (<players>). <registered>/<max> registered for the next round (<min> start it).", { players: inIt, registered, max, min })
							: t("A round is being played (<players>). Register now for the next round.", { players: inIt });
		return { kind: full ? "full" : "in_progress", label: full ? t("Full") : t("In progress"), detail };
	}
	if (full) return { kind: "full", label: t("Full"), detail: t("All <max> seats are taken: the round starts in a moment.", { max }) };
	if (counting) {
		const time = countdownText(left);
		if (left > 0)
			return {
				kind: "starting",
				label: t("Starting <time>", { time }),
				detail: paused
					? t("<registered>/<max> players: the round starts in <time>. Registration is paused: no new players.", { registered, max, time })
					: t("<registered>/<max> players: the round starts in <time>.", { registered, max, time }),
			};
		return {
			kind: "starting",
			label: t("Starting"),
			detail: paused
				? t("<registered>/<max> players: the round starts as soon as a game is ready. Registration is paused: no new players.", { registered, max })
				: t("<registered>/<max> players: the round starts as soon as a game is ready.", { registered, max }),
		};
	}
	if (paused) return { kind: "paused", label: t("Paused"), detail: t("Registration is paused for now: the room opens again soon.") };
	if (!registered) return { kind: "open", label: t("Open"), detail: t("Nobody yet: <min> players start the countdown.", { min }) };
	const more = min - registered;
	return more > 0
		? { kind: "open", label: t("Open · <registered>/<min>", { registered, min }), detail: t("<players> registered: <more> more start the countdown.", { players: players(registered), more }) }
		: { kind: "open", label: t("Open · <registered>/<max>", { registered, max }), detail: t("<players> registered: the countdown starts in a moment.", { players: players(registered) }) };
}
